import React, { memo } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { usePagination, UsePaginationReturn } from '../../hooks/usePagination';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  gap: 16px;
  flex-wrap: wrap;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
`;

const PageSizeSelect = styled.select`
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PaginationButton = styled.button<{ 
  variant?: 'primary' | 'secondary' | 'ellipsis';
  disabled?: boolean;
}>`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
          &:hover:not(:disabled) {
            background: #2563eb;
            border-color: #2563eb;
          }
        `;
      case 'ellipsis':
        return `
          background: transparent;
          color: #6b7280;
          border: none;
          cursor: default;
          &:hover {
            background: transparent;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          &:hover:not(:disabled) {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f9fafb;
    color: #9ca3af;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const EllipsisIcon = styled(MoreHorizontal)`
  width: 16px;
  height: 16px;
`;

export interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  showInfo?: boolean;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 25, 50, 100],
  showInfo = true,
  className
}) => {
  const pagination = usePagination({
    initialPage: currentPage,
    initialItemsPerPage: itemsPerPage,
    totalItems
  });

  const handlePageChange = (page: number) => {
    onPageChange(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    if (onItemsPerPageChange) {
      onItemsPerPageChange(newItemsPerPage);
    }
  };

  const startItem = pagination.startIndex + 1;
  const endItem = pagination.endIndex;

  return (
    <PaginationContainer className={className}>
      {showInfo && (
        <PaginationInfo>
          Showing {startItem} to {endItem} of {totalItems} entries
        </PaginationInfo>
      )}

      <PaginationControls>
        {showPageSizeSelector && onItemsPerPageChange && (
          <PageSizeSelector>
            <span>Show</span>
            <PageSizeSelect
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PageSizeSelect>
            <span>entries</span>
          </PageSizeSelector>
        )}

        <PaginationButtons>
          <PaginationButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={pagination.isFirstPage}
            title="Previous page"
          >
            <ChevronLeft size={16} />
          </PaginationButton>

          {pagination.pageNumbers.map((pageNumber, index) => {
            if (pageNumber === -1) {
              return (
                <PaginationButton
                  key={`ellipsis-${index}`}
                  variant="ellipsis"
                  disabled
                >
                  <EllipsisIcon size={16} />
                </PaginationButton>
              );
            }

            return (
              <PaginationButton
                key={pageNumber}
                variant={pageNumber === currentPage ? 'primary' : 'secondary'}
                onClick={() => handlePageChange(pageNumber)}
                title={`Go to page ${pageNumber}`}
              >
                {pageNumber}
              </PaginationButton>
            );
          })}

          <PaginationButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={pagination.isLastPage}
            title="Next page"
          >
            <ChevronRight size={16} />
          </PaginationButton>
        </PaginationButtons>
      </PaginationControls>
    </PaginationContainer>
  );
};

export default React.memo(Pagination);
